import type { ReactNode } from "react";
import { useId } from "react";

import { Flex, Text } from "@radix-ui/themes";

export interface FieldControlProps {
  id: string;
  "aria-describedby": string | undefined;
  "aria-invalid": true | undefined;
  required: boolean | undefined;
}

export interface FieldProps {
  label: string;
  /**
   * Receives the props the control has to carry, so that labelling, the
   * description and the error state cannot drift from what is rendered.
   */
  children: (control: FieldControlProps) => ReactNode;
  hint?: ReactNode;
  /** When set, replaces the hint and is announced as the field's error. */
  error?: ReactNode;
  required?: boolean;
}

/**
 * Label + control + hint/error, wired together for accessibility.
 *
 * Radix Themes has no field wrapper of its own — it ships the controls and
 * leaves the labelling to the app — so this is the one piece the Figma's forms
 * page needs that the library does not provide.
 */
export const Field = ({
  label,
  children,
  hint,
  error,
  required,
}: FieldProps) => {
  const id = useId();
  const messageId = `${id}-message`;
  const message = error ?? hint;

  return (
    <Flex direction="column" gap="1">
      <Text as="label" size="2" weight="medium" htmlFor={id}>
        {label}
        {required ? (
          <Text color="red" aria-hidden>
            {" *"}
          </Text>
        ) : null}
      </Text>

      {children({
        id,
        "aria-describedby": message ? messageId : undefined,
        "aria-invalid": error ? true : undefined,
        required,
      })}

      {message ? (
        <Text
          id={messageId}
          size="1"
          color={error ? "red" : "gray"}
          role={error ? "alert" : undefined}
        >
          {message}
        </Text>
      ) : null}
    </Flex>
  );
};
